import { memo, ReactNode } from 'react'
import { Link } from 'react-router-dom'

import clsx from 'clsx'

import {
    Divider,
    ListItem,
    ListItemIcon,
    ListItemText,
    makeStyles,
    Tooltip,
    Typography,
    Box
} from '@material-ui/core'
import { CSSProperties } from '@material-ui/styles'

import { ReactComponent as InviteIcon } from 'assets/icons/menu/icon-invite.svg'

import { TextOverflow } from '@guidde-co/shared.text-overflow.text-overflow'

import { InviteDialog, useMenuStatus } from 'UI/Components'

import { useBoolean, useWindowView } from 'hooks'
import { LocationDescriptor } from 'history'

type StyleProps = {
    activeColor: CSSProperties['color']
}

const useStyles = makeStyles(theme => ({
    gutters: {
        padding: theme.spacing(1, 2.5)
    },
    listItemText: {
        color: '#78909c',
        [theme.breakpoints.down('sm')]: {
            color: '#828282'
        },
        textDecoration: 'none !important'
    },
    title: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    listItemIcon: {
        color: '#78909c',
        [theme.breakpoints.down('sm')]: {
            color: '#828282'
        },
        minWidth: '25px',
        marginRight: theme.spacing(1)
    },
    link: {
        textDecoration: 'none',
        maxWidth: '240px',
        position: 'relative'
    },
    activeLink: ({ activeColor }: StyleProps) => ({
        color: activeColor,
        '&:hover': {
            color: activeColor
        }
    }),
    tooltipPopper: {
        zIndex: theme.zIndex.drawer + 1
    }
}))

type Props = {
    id?: string
    to: LocationDescriptor<{ prevPath?: string }>
    activeColor?: CSSProperties['color']
    isActive: boolean
    title: string
    startIcon?: ReactNode
    endIcon?: ReactNode
    showTooltip?: boolean
    divider?: boolean
}

export const DrawerLink = memo(
    ({
        id,
        to,
        isActive,
        divider,
        startIcon,
        endIcon,
        title,
        activeColor = '#cd0000',
        showTooltip
    }: Props) => {
        const classes = useStyles({ activeColor })
        const { isMenuClosed } = useMenuStatus()

        const { isMobileView } = useWindowView()

        return (
            <Link
                to={to}
                className={clsx(classes.link, {
                    [classes.activeLink]: isActive
                })}
                id={id}
            >
                {divider && (
                    <Box px={2}>
                        <Divider />
                    </Box>
                )}
                <ListItem button classes={{ gutters: classes.gutters }}>
                    {startIcon && (
                        <ListItemIcon className={classes.listItemIcon}>{startIcon}</ListItemIcon>
                    )}
                    <ListItemText
                        className={clsx(classes.listItemText, {
                            [classes.activeLink]: isActive
                        })}
                        primary={
                            <TextOverflow>
                                <Tooltip
                                    title={title}
                                    disableHoverListener={!showTooltip}
                                    classes={{ popper: classes.tooltipPopper }}
                                >
                                    <Typography
                                        style={{ marginLeft: isMobileView ? 8 : 0 }}
                                        className={classes.title}
                                    >
                                        {title}
                                    </Typography>
                                </Tooltip>
                            </TextOverflow>
                        }
                    />

                    {endIcon && !isMenuClosed && (
                        <ListItemIcon className={classes.listItemIcon}>{endIcon}</ListItemIcon>
                    )}
                </ListItem>
            </Link>
        )
    }
)

export const DrawerInviteItem = () => {
    const classes = useStyles({ activeColor: '#cd0000' })

    const open = useBoolean()

    return (
        <>
            <ListItem button classes={{ gutters: classes.gutters }} onClick={open.setTrue}>
                <ListItemIcon className={classes.listItemIcon}>
                    <InviteIcon />
                </ListItemIcon>
                <ListItemText
                    className={classes.listItemText}
                    primary={
                        <TextOverflow>
                            <Typography data-cy="drawerlink-invite" className={classes.title}>
                                Invite
                            </Typography>
                        </TextOverflow>
                    }
                />
            </ListItem>
            {open.isTrue && <InviteDialog isOpen={true} onClose={open.setFalse} />}
        </>
    )
}
